import { Fragment, useMemo, useState } from "react";
import { ItemAssemblyPicker } from "./ItemAssemblyPicker";
import FormulaExpressionEditor, { formulaStatus } from "./FormulaExpressionEditor";
import ItemQuickEditWindow from "./ItemQuickEditWindow";
import { indexComponents, flattenAssembly, quantityExpression, isPlainNumber, libraryFormulaValues, DISPLAY_MODES } from "../utils/assemblyComponents";
import { flattenVariableGroups, checkExpression } from "../utils/formulaAssist";
import { buildFormulaVariableGroups, ENGINE_VARIABLES_GROUP_KEY } from "../config/formulaVariables";
import { engineVariableBindings } from "../utils/roomFormulaEngine";

// The component list of ONE assembly (db/88), shown on the Assembly form. The list lives on the form's draft:
//   draft._components        undefined = untouched (the saved rows show), else [{ kind, id, formula, notes }]
//   draft._componentDisplay  undefined = untouched, else 'by_cost_code' | 'single_line'
// The page saves both through sp_SaveEstimateAssemblyComponents after the assembly itself.
//
// Every component's quantity is ONE line of text:
//   4                     the takeoff quantity × 4          (a plain number is a multiplier)
//   qty * 4 · ceil(qty / 10)   a formula; `qty` is the assembly's own takeoff quantity, in the assembly's UOM
//   PULLBOX * 4           a library formula (Setup ▸ Estimate Engine ▸ Formulas) used BY ITS CODE, like any variable
// Engine Variables can be used by name; room variables cannot.

const DEFAULT_SAMPLE_QTY = 25; // the formula check's starting qty, chosen so a ceil(qty / 10) shows a visible step

const box = { gridColumn: "span 4", border: "1px solid var(--cs-border)", borderRadius: 8, padding: "8px 10px", background: "var(--surface)" };
const cell = { padding: "2px 6px", fontSize: 12, borderBottom: "1px solid var(--cs-border)", verticalAlign: "top" };
const inputSm = { fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4 };
const usd = (n) => (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

// The rows saved for an assembly, in the editor's shape: the formula text, or — for the original quantity-per-unit model — the number.
export function savedComponentRows(componentRows, assemblyId) {
  if (assemblyId == null) return [];
  return (componentRows || []).filter(r => r.AssemblyID === assemblyId).map(r => ({
    kind: r.ChildKind, id: r.ChildKind === "item" ? r.ChildItemID : r.ChildAssemblyID, notes: r.Notes || "",
    formula: r.QtyFormula ? r.QtyFormula : String(Number(r.QtyPerUnit) || 1),
  }));
}

export function componentsPayload(draft, componentRows) {
  const rows = draft._components ?? savedComponentRows(componentRows, draft.AssemblyID);
  const payload = {
    assembly_id: draft.AssemblyID,
    components: rows.map(r => {
      const text = String(r.formula ?? "").trim() || "1"; // blank = one per unit
      const plain = isPlainNumber(text) && Number(text) > 0;
      return {
        child_kind: r.kind, item_id: r.kind === "item" ? r.id : null, assembly_id: r.kind === "assembly" ? r.id : null,
        // a plain number is stored as the original quantity-per-unit; anything else is the formula text
        qty_per_unit: plain ? Number(text) : 1, formula: plain ? null : text, notes: r.notes || null,
      };
    }),
  };
  if (draft._componentDisplay !== undefined) payload.component_display = draft._componentDisplay;
  return payload;
}

export default function AssemblyComponentsEditor({ draft, setDraft, items = [], assemblies = [], componentRows = [], categories = [], formulas = [], engineVariableRows = [], costCodes = [], onSaveItem = null, uoms = [], onAddUom = null }) {
  const rows = draft._components ?? savedComponentRows(componentRows, draft.AssemblyID);
  const display = draft._componentDisplay !== undefined ? (draft._componentDisplay || DISPLAY_MODES.byCostCode) : (draft.ComponentDisplay || DISPLAY_MODES.byCostCode);
  const uom = draft.UOM || "unit";
  const [editingItemId, setEditingItemId] = useState(null); // the catalog Item open in the floating edit window
  // The quantity the formulas are tried at, so an author can see the result (editable; the real quantity comes from the takeoff).
  const [sampleQtyText, setSampleQtyText] = useState(String(DEFAULT_SAMPLE_QTY));
  const sampleQty = Number.isFinite(+sampleQtyText) && +sampleQtyText > 0 ? +sampleQtyText : DEFAULT_SAMPLE_QTY;
  const itemsById = useMemo(() => new Map(items.map(i => [i.ItemID, i])), [items]);
  const assembliesById = useMemo(() => new Map(assemblies.map(a => [a.AssemblyID, a])), [assemblies]);
  const entityOf = (r) => (r.kind === "item" ? itemsById.get(r.id) : assembliesById.get(r.id));

  // What a formula can use here: qty, the Engine Variables, and the library formulas that can be calculated from those alone.
  const engineBindings = useMemo(() => engineVariableBindings(Object.fromEntries(engineVariableRows.map(r => [r.VariableKey, +r.Value]))), [engineVariableRows]);
  const libraryValues = useMemo(() => libraryFormulaValues(formulas, sampleQty, engineBindings), [formulas, sampleQty, engineBindings]);
  const bound = useMemo(() => ({ ...libraryValues, ...engineBindings, qty: sampleQty }), [libraryValues, engineBindings, sampleQty]);
  const variables = useMemo(() => {
    const engine = flattenVariableGroups(buildFormulaVariableGroups(engineVariableRows).filter(g => g.key === ENGINE_VARIABLES_GROUP_KEY));
    const library = formulas.filter(f => f.Code in libraryValues).map(f => ({ name: f.Code, unit: f.OutputUnit || "", desc: `${f.Name} — ${f.Expression}`, group: "library", groupTitle: "Library formula" }));
    return [{ name: "qty", unit: uom, desc: "The quantity the estimator takes off for this assembly", group: "assembly", groupTitle: "This assembly" }, ...library, ...engine];
  }, [engineVariableRows, formulas, libraryValues, uom]);
  const sampleLabel = `qty = ${sampleQty} ${uom}`;
  // One-click starting points (each replaces the formula): one per unit, a multiplier, a stepped count — plus the library formulas usable here, by code.
  const quickFormulas = useMemo(() => [
    { text: "1", hint: "One per unit: the takeoff quantity × 1" },
    { text: "qty * 4", hint: "A multiplier written as a formula" },
    { text: "ceil(qty / 10)", hint: "One for every 10 (rounded up)" },
    ...formulas.filter(f => f.Code in libraryValues).slice(0, 6).map(f => ({ text: f.Code, hint: `Library formula: ${f.Name} — ${f.Expression}` })),
  ], [formulas, libraryValues]);

  const setRows = (next, extra = {}) => setDraft({ ...draft, _components: next, ...extra });
  const update = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const remove = (i) => setRows(rows.filter((_, j) => j !== i));
  const add = (e) => {
    if (e.kind === "assembly" && e.id === draft.AssemblyID) return; // an assembly cannot contain itself
    setRows([...rows, { kind: e.kind, id: e.id, notes: "", formula: "1" }]);
  };

  // The rows as component records (what the resolver reads).
  const rowsById = draft.AssemblyID ?? -1;
  const resolverCtx = useMemo(() => {
    const records = rows.map((r, i) => ({
      ComponentID: i + 1, AssemblyID: rowsById, ChildKind: r.kind, DisplayOrder: i + 1, QtyPerUnit: 1,
      ChildItemID: r.kind === "item" ? r.id : null, ChildAssemblyID: r.kind === "assembly" ? r.id : null,
      QtyFormula: String(r.formula ?? "").trim() || "1",
    }));
    const byAsm = indexComponents([...(componentRows || []).filter(c => c.AssemblyID !== draft.AssemblyID), ...records]);
    return {
      componentsByAssembly: byAsm, itemsById, engineVars: engineBindings, libraryFormulas: formulas,
      assembliesById: new Map([...assembliesById, [rowsById, { AssemblyID: rowsById, Description: draft.Description || "this assembly" }]]),
    };
  }, [rows, componentRows, draft.AssemblyID, draft.Description, itemsById, assembliesById, engineBindings, formulas, rowsById]);

  // What the Estimate will show: one line per cost code the flattened components sit under (or one line), at the sample qty.
  const preview = useMemo(() => {
    if (!rows.length) return null;
    const flat = flattenAssembly(rowsById, resolverCtx, sampleQty);
    if (!flat.ok) return { warnings: flat.warnings, groups: [] };
    const groups = new Map();
    for (const l of flat.leaves) {
      const key = display === DISPLAY_MODES.singleLine ? "all" : String(l.entity.CostCodeID ?? "none");
      const g = groups.get(key) || { label: display === DISPLAY_MODES.singleLine ? "all components" : (l.entity.CSICode ? `${l.entity.CSICode}${l.entity.CostCodeDescription ? ` ${l.entity.CostCodeDescription}` : ""}` : "no cost code"), count: 0 };
      g.count += 1;
      groups.set(key, g);
    }
    return { warnings: [], groups: [...groups.values()] };
  }, [rows, display, resolverCtx, rowsById, sampleQty]);

  return (
    <div style={box} data-testid="assembly-components-editor">
      {editingItemId != null && itemsById.get(editingItemId) && onSaveItem && (
        <ItemQuickEditWindow key={editingItemId} item={itemsById.get(editingItemId)} costCodes={costCodes} uoms={uoms} onAddUom={onAddUom} onSave={edits => onSaveItem(itemsById.get(editingItemId), edits)} onClose={() => setEditingItemId(null)} />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
        <strong style={{ fontSize: 12.5 }}>Components</strong>
        <span style={{ fontSize: 11, color: "var(--text3)", flex: 1, minWidth: 260 }}>
          Quantity formula: <code>qty</code> = the takeoff quantity for this assembly, <strong>in {uom}</strong>. <code>4</code> = takeoff × 4 · <code>ceil(qty / 10)</code> · library formula by code, <code>PULLBOX * 4</code>.
          Costs here are read-only; each component keeps its own markup and rounding.
        </span>
        {rows.length > 0 && (
          <label style={{ fontSize: 12 }} title="Formulas are tried at this quantity so you can check them; the real quantity comes from the takeoff">Try at qty{" "}
            <input type="number" min="0" step="any" value={sampleQtyText} onChange={ev => setSampleQtyText(ev.target.value)} style={{ ...inputSm, width: 64, textAlign: "right" }} aria-label="Preview quantity" /> {uom}
          </label>
        )}
        <label style={{ fontSize: 12 }}>Estimate shows{" "}
          <select value={display} onChange={e => setRows(rows, { _componentDisplay: e.target.value })} style={inputSm}>
            <option value={DISPLAY_MODES.byCostCode}>one line per cost code</option>
            <option value={DISPLAY_MODES.singleLine}>a single line</option>
          </select>
        </label>
      </div>

      {rows.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 6 }}>
          <thead><tr>
            {["", "Component", "Cost code", "Unit", "Material $", "Labor hr", "Vendor $", "Note", ""].map((h, i) => (
              <th key={i} style={{ ...cell, textAlign: i >= 4 && i <= 6 ? "right" : "left", fontSize: 11, color: "var(--text3)" }}>{h}</th>))}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const e = entityOf(r);
              const top = { ...cell, borderBottom: "none", paddingTop: 5 };
              const status = formulaStatus(checkExpression(quantityExpression(r.formula ?? ""), { vars: variables, bindings: engineBindings, bound }), { bound, sampleLabel });
              const TONE = { ok: "#166534", warn: "#b45309", bad: "#b91c1c" };
              const ICON = { ok: "✓", warn: "⚠", bad: "✗" };
              return (
                <Fragment key={`${r.kind}:${r.id}:${i}`}>
                  <tr>
                    <td style={top}><span style={{ fontSize: 9, fontWeight: 700, padding: "0 5px", borderRadius: 100, background: r.kind === "assembly" ? "#e0e7ff" : "#dcfce7", color: r.kind === "assembly" ? "#4338ca" : "#15803d" }}>{r.kind === "assembly" ? "ASM" : "ITEM"}</span></td>
                    <td style={top}>{e ? <><span style={{ fontFamily: "monospace", fontWeight: 600 }}>{e.Code}</span> {r.kind === "item" ? e.ItemName : e.Description}</> : <span style={{ color: "#b91c1c" }}>Missing or retired (#{r.id})</span>}</td>
                    <td style={{ ...top, fontFamily: "monospace", whiteSpace: "nowrap" }} title={e?.CostCodeDescription || ""}>{e?.CSICode || <span style={{ color: "var(--text3)" }}>none</span>}</td>
                    <td style={top}>{e?.UOM}</td>
                    <td style={{ ...top, textAlign: "right", whiteSpace: "nowrap" }}>{e ? usd(e.MaterialCostPerUnit) : "—"}</td>
                    <td style={{ ...top, textAlign: "right", whiteSpace: "nowrap" }} title="Labor is stored in hours; dollars depend on each estimate's labor rate">{e ? `${(+e.LaborHrPerUnit || 0).toFixed(3)} hr` : "—"}</td>
                    <td style={{ ...top, textAlign: "right", whiteSpace: "nowrap" }}>{e ? usd(e.VendorSubCostPerUnit) : "—"}</td>
                    <td style={top}><input value={r.notes} maxLength={200} onChange={ev => update(i, { notes: ev.target.value })} style={{ ...inputSm, width: 120 }} aria-label="Note" /></td>
                    <td style={{ ...top, whiteSpace: "nowrap" }}>
                      {e && r.kind === "item" && onSaveItem && <span onClick={() => setEditingItemId(r.id)} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 8 }} title="Edit this catalog item's name, cost code, unit and costs in a floating window">Edit item</span>}
                      <span onClick={() => remove(i)} style={{ cursor: "pointer", color: "var(--text3)" }}>Remove</span>
                    </td>
                  </tr>
                  <tr data-testid="quantity-formula-row">
                    <td style={{ ...cell, background: "var(--surface2)", textAlign: "center", fontSize: 15, fontWeight: 700, color: status ? TONE[status.tone] : "var(--text3)" }} data-testid="formula-check" title={status?.text || "Enter a quantity formula"} aria-label={status ? (status.tone === "ok" ? "Formula OK" : "Formula problem") : "No formula"}>{status ? ICON[status.tone] : "·"}</td>
                    <td style={{ ...cell, background: "var(--surface2)", paddingBottom: 5 }} colSpan={8}>
                      <div style={{ display: "grid", gridTemplateColumns: "120px minmax(200px, 1fr) minmax(0, auto) auto", gap: 8, alignItems: "center" }}>
                        <label style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text2)" }} htmlFor={`qty-formula-${i}`}>Quantity formula</label>
                        <div>
                          <FormulaExpressionEditor value={r.formula ?? ""} onChange={v => update(i, { formula: v })} variables={variables} bindings={engineBindings}
                            bound={bound} sampleLabel={sampleLabel} evaluateAs={quantityExpression} style={{ ...inputSm, fontSize: 12.5, padding: "3px 8px" }} hideStatus inputId={`qty-formula-${i}`}
                            placeholder="1   ·   4   ·   qty * 4   ·   ceil(qty / 10)" />
                        </div>
                        <div data-testid="formula-result" style={{ fontSize: 11.5, color: status ? TONE[status.tone] : "var(--text3)", maxWidth: 300, lineHeight: 1.25 }}>{status?.text}</div>
                        <div data-testid="quick-set" style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end", maxWidth: 330 }}>
                          <span style={{ fontSize: 10.5, color: "var(--text3)" }}>Quick set:</span>
                          {quickFormulas.map(q => (
                            <button key={q.text} type="button" onClick={() => update(i, { formula: q.text })} title={q.hint}
                              style={{ fontSize: 11, padding: "1px 8px", borderRadius: 100, border: "1px solid var(--cs-border)", background: "var(--surface)", cursor: "pointer", fontFamily: "monospace" }}>{q.text}</button>))}
                        </div>
                      </div>
                    </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ minWidth: 320, flex: 1 }}>
          <ItemAssemblyPicker items={items} assemblies={assemblies.filter(a => a.AssemblyID !== draft.AssemblyID)} categories={categories} value={null} onSelect={add} />
        </div>
        <div style={{ fontSize: 11.5, color: "var(--text2)" }} data-testid="assembly-components-preview">
          {!rows.length && "No components — this assembly is one flat priced line."}
          {preview?.warnings?.length > 0 && <span style={{ color: "#b91c1c" }}>{preview.warnings[0].message} It will stay a flat line until fixed.</span>}
          {preview?.groups?.length > 0 && <>At {sampleLabel} the Estimate will show <strong>{preview.groups.length}</strong> line{preview.groups.length === 1 ? "" : "s"}: {preview.groups.map(g => `${g.label} (${g.count})`).join(" · ")}</>}
        </div>
      </div>
    </div>
  );
}
